import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { discoverOdata } from "@/api/odata";
import {
  deleteMpBuyout,
  listMpBuyouts,
  uploadMpBuyout,
} from "@/api/marketplace_buyouts";
import { useConnection } from "@/auth/ConnectionContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { formatRuDate, formatRuDateTime } from "@/lib/format";
import { extractErrorMessage } from "@/lib/api-errors";
import { SELECT_CLASSES } from "@/lib/styles";
import type { OrgDTO } from "@/types/api";

function StatusChip({ status }: { status: string }): React.ReactElement {
  const map: Record<string, { label: string; cls: string }> = {
    uploaded: { label: "Загружено", cls: "bg-slate-100 text-slate-700" },
    created: { label: "Создан в 1С", cls: "bg-emerald-100 text-emerald-700" },
    error: { label: "Ошибка", cls: "bg-rose-100 text-rose-700" },
  };
  const v = map[status] ?? { label: status, cls: "bg-slate-100 text-slate-700" };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${v.cls}`}
    >
      {v.label}
    </span>
  );
}

export function MarketplaceBuyoutsPage(): React.ReactElement {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { activeConnectionId: connId } = useConnection();
  const [showAdd, setShowAdd] = useState(false);

  const buyoutsQ = useQuery({
    queryKey: ["mp-buyouts", connId],
    queryFn: listMpBuyouts,
    enabled: !!connId,
  });

  const deleteMut = useMutation({
    mutationFn: deleteMpBuyout,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mp-buyouts", connId] });
      toast.success("Уведомление удалено локально (документ в 1С остался)");
    },
    onError: (err) => toast.error("Не удалось удалить", { description: String(err) }),
  });

  return (
    <div className="space-y-6 p-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Выкупы МП</h1>
          <p className="text-sm text-muted-foreground">
            Загрузка XLSX-уведомлений о выкупе товара комиссионером и создание
            документов выкупа в УНФ и Бухгалтерии.
          </p>
        </div>
        <Button onClick={() => setShowAdd(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Добавить уведомление
        </Button>
      </header>

      {showAdd && <AddBuyoutModal onClose={() => setShowAdd(false)} />}

      {buyoutsQ.isLoading && (
        <p className="text-sm text-muted-foreground">Загрузка…</p>
      )}
      {buyoutsQ.isError && (
        <p className="text-sm text-rose-600">
          Не удалось загрузить список: {extractErrorMessage(buyoutsQ.error, "нет ответа")}
        </p>
      )}

      {buyoutsQ.data && buyoutsQ.data.length === 0 && (
        <div className="rounded-md border border-dashed p-10 text-center">
          <p className="text-sm text-muted-foreground">
            Уведомлений пока нет. Нажмите «Добавить уведомление».
          </p>
        </div>
      )}

      {buyoutsQ.data && buyoutsQ.data.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Файл</TableHead>
              <TableHead>Организация</TableHead>
              <TableHead>МП</TableHead>
              <TableHead>Дата уведомления</TableHead>
              <TableHead>Статус</TableHead>
              <TableHead className="w-32">Загружено</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {buyoutsQ.data.map((r) => (
              <TableRow
                key={r.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => navigate(`/mp-buyouts/${r.id}`)}
              >
                <TableCell className="max-w-md truncate" title={r.source_filename}>
                  {r.source_filename}
                </TableCell>
                <TableCell>{r.organization_name}</TableCell>
                <TableCell className="uppercase text-xs">{r.mp_code}</TableCell>
                <TableCell>
                  {r.notice_date ? formatRuDate(r.notice_date) : "—"}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <StatusChip status={r.status} />
                    {r.documents.map((d) => (
                      <span
                        key={d.kind}
                        className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700"
                        title={
                          (d.connection_name
                            ? `База: ${d.connection_name}`
                            : "База неизвестна") +
                          ` · ${d.posted ? "проведён" : "не проведён"}`
                        }
                      >
                        {d.base === "unf" ? "УНФ" : "БП"}
                        {d.connection_name ? ` · ${d.connection_name}` : ""}
                        : {d.one_c_number ?? "1С"}
                      </span>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {formatRuDateTime(r.created_at)}
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirm("Удалить уведомление локально? Документ в 1С останется.")) {
                        deleteMut.mutate(r.id);
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-rose-500" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function AddBuyoutModal({ onClose }: { onClose: () => void }): React.ReactElement {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { activeConnectionId: connId } = useConnection();
  const [organization, setOrganization] = useState<OrgDTO | null>(null);
  const [mpCode] = useState<string>("wb");
  const [file, setFile] = useState<File | null>(null);

  const orgsQ = useQuery({
    queryKey: ["odata-orgs", connId],
    queryFn: async () => (await discoverOdata()).organizations,
    enabled: !!connId,
  });

  const uploadMut = useMutation({
    mutationFn: () =>
      uploadMpBuyout({
        file: file!,
        mp_code: mpCode,
        organization_key: organization!.ref_key,
        organization_name: organization!.description,
      }),
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: ["mp-buyouts", connId] });
      onClose();
      if (resp.duplicate_of) {
        toast.info("Файл уже загружался — открываем существующее уведомление");
      } else {
        toast.success("Уведомление загружено");
      }
      navigate(`/mp-buyouts/${resp.id}`);
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        String(err);
      toast.error("Не удалось загрузить", { description: detail });
    },
  });

  const canSubmit = !!organization && !!file && !uploadMut.isPending;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Новое уведомление о выкупе</DialogTitle>
          <DialogDescription>
            Загрузите XLSX-уведомление о выкупе товара комиссионером —
            мы создадим из него проведённую «Расходную накладную» в 1С УНФ.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="org">Организация</Label>
            <select
              id="org"
              className={SELECT_CLASSES}
              value={organization?.ref_key ?? ""}
              onChange={(e) => {
                const o = orgsQ.data?.find((x) => x.ref_key === e.target.value);
                setOrganization(o ?? null);
              }}
              disabled={orgsQ.isLoading}
            >
              <option value="">
                {orgsQ.isLoading ? "Загрузка списка организаций…" : "— выберите —"}
              </option>
              {orgsQ.data?.map((o) => (
                <option key={o.ref_key} value={o.ref_key}>
                  {o.description}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="mp">Маркетплейс</Label>
            <select
              id="mp"
              className={cn(SELECT_CLASSES, "cursor-not-allowed opacity-70")}
              value={mpCode}
              disabled
            >
              <option value="wb">Wildberries</option>
            </select>
            <p className="text-xs text-muted-foreground">Озон — позже.</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="file">Файл XLSX</Label>
            <Input
              id="file"
              type="file"
              accept=".xlsx"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            {file && (
              <p className="text-xs text-muted-foreground">
                {file.name} · {(file.size / 1024 / 1024).toFixed(1)} МБ
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button disabled={!canSubmit} onClick={() => uploadMut.mutate()}>
            {uploadMut.isPending ? "Загрузка…" : "Загрузить"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
